import { Trans } from "@lingui/react/macro";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";

import {
  AuditRoomRow,
  MedicalWaitingRoomAlerts,
  WaitingRoomEvents,
} from "../../api";
import {
  eventCategory,
  eventCategoryDetails,
  eventCategoryType,
} from "../../features/patient/events/enums";
import { DataModel } from "../../models/data-model";
import { PopoverContent } from "../ui-kit/popover";
import { useCloseOnScroll } from "../ui-kit/tables/cells/use-close-on-scroll";
import { DataTable } from "../ui-kit/tables/data-table";
import styles from "./cell-events.module.css";

export const CellEvents = observer(function CellEvents({
  item,
}: {
  item: MedicalWaitingRoomAlerts | AuditRoomRow;
}) {
  const [open, setOpen] = useState(false);
  useCloseOnScroll(open, setOpen);

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger className={styles.trigger}>
        {item.last_events
          .slice()
          .sort(
            (a, b) =>
              new Date(b.event_date).getTime() -
              new Date(a.event_date).getTime(),
          )
          .map((event, i) => (
            <EventSymbol key={i} event={event} />
          ))}
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverContent>
          <EventsTable events={item.last_events} />
        </PopoverContent>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
});

const EventsTable = observer(function EventsTable({
  events,
}: {
  events: WaitingRoomEvents[];
}) {
  // WaitingRoomEvents has no id field — synthesize an index-based row key.
  const [model] = useState(() => {
    const m = new DataModel(events.map((event, key) => ({ ...event, key })));
    m.init();
    return m;
  });

  return (
    <DataTable
      model={model}
      itemKeyField="key"
      columns={[
        {
          header: "",
          cell: ({ item }) => <EventSymbol event={item} />,
        },
        {
          header: <Trans>Date</Trans>,
          attr: "event_date",
          type: "date",
          sort: true,
          initSort: "desc",
        },
        {
          header: <Trans>Category</Trans>,
          attr: "event_category",
          type: "enum",
          values: eventCategory,
        },
        {
          header: <Trans>Category Type</Trans>,
          attr: "event_category_type",
          type: "enum",
          values: eventCategoryType,
        },
        {
          header: <Trans>Category Details</Trans>,
          attr: "event_category_details",
          type: "enum",
          values: eventCategoryDetails,
        },
        {
          header: <Trans>Description</Trans>,
          attr: "event_description",
          type: "richtext",
          // Without a width the cell never truncates: the table lays columns
          // out at their content's intrinsic width, so a long description
          // would stretch the column as one endless line.
          width: 330,
        },
        {
          header: <Trans>Added By</Trans>,
          attr: "added_by",
          type: "string",
        },
      ]}
      rowsPerPage={Number.MAX_VALUE}
    />
  );
});

function EventSymbol({ event }: { event: WaitingRoomEvents }) {
  return (
    <EventMarker shape={event.event_shape} colorHex={event.event_color_hex} />
  );
}

export function EventMarker({
  shape,
  colorHex,
}: {
  shape: string;
  colorHex: string;
}) {
  const renderShape = () => {
    switch (shape) {
      case "circle":
        return (
          <span className={styles.symbol} style={{ top: "1px" }}>
            ●
          </span>
        );
      case "star":
        return (
          <span className={`${styles.symbol} text-2xs`} style={{ top: "2px" }}>
            ★
          </span>
        );
      case "triangle-up":
        return <span className={styles.symbol}>▲</span>;
      case "triangle-down":
        return <span className={styles.symbol}>▼</span>;
      case "square":
        return <span className={styles.symbol}>■</span>;
      case "diamond":
        return (
          <span className={styles.symbol} style={{ top: "-1px" }}>
            ◆
          </span>
        );
      case "cross":
        return <span className={styles.symbol}>✚</span>;
      case "pentagon":
        return <span className={styles.symbol}>⬟</span>;
      case "arrow-bar-right":
        return <span className={styles.symbol}>↦</span>;
      case "hourglass":
        return (
          <span className={styles.symbol} style={{ top: "1px" }}>
            ⧗
          </span>
        );
      case "asterisk":
        return <span className={styles.symbol}>*</span>;
      case "x":
        return <span className={styles.symbol}>✕</span>;
      default:
        return null;
    }
  };

  return (
    <div className={styles.symbolContainer} style={{ color: colorHex }}>
      {renderShape()}
    </div>
  );
}
